import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { ContentService } from 'src/app/content-management/shared/services/content.service';
import { SearchArticleRequest } from 'src/app/core/models/i-search-article-request';
import { ISearchContentResponse } from 'src/app/core/models/i-search-content-response';
import {
  IRelatedContent,
  RelatedContent,
} from 'src/app/core/models/i-related-content';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { EMPTY, Observable, Subject, of } from 'rxjs';
import { IParseArticleUrlResponse } from 'src/app/core/models/i-parse-article-url-response';
import { ArticleStatus } from 'src/app/content-management/shared/enums/article-status';
import { MatLegacyAutocompleteTrigger } from '@angular/material/legacy-autocomplete';
import {
  ISearchArticleResult,
  SearchArticleResult,
} from '../../interfaces/i-search-article-result';
import { ContentType } from 'src/app/content-management/shared/enums/content-type';
import { IDashboardContent } from 'src/app/core/models/i-dashboard-content';
import { ILinkInsert } from 'src/app/core/dtos/interfaces/i-link-insert';
import { DataLinkType } from 'src/app/core/dtos/enums/data-link-type';

@Component({
  selector: 'app-related-articles',
  templateUrl: './related-articles.component.html',
  styleUrls: ['./related-articles.component.scss'],
})
export class RelatedArticlesComponent implements OnInit, OnDestroy {
  @Input() isExpandable: boolean = true;
  @Input() isExpandedByDefault: boolean = false;
  @Input() relatedContents: FormArray;
  @Input() editions: string[];

  @Output() handleAddToBody = new EventEmitter<ILinkInsert>();
  @Output() handleRemoveLink = new EventEmitter<{
    regex: string;
    newValue: string;
  }>();

  @ViewChild(MatLegacyAutocompleteTrigger)
  matAutocomplete: MatLegacyAutocompleteTrigger;

  DEFAULT_PAGE_NUMBER: number = 1;
  destroy$ = new Subject<boolean>();

  searchList: ISearchArticleResult[] = [];
  searchBarControl: FormControl;
  addArticleByUrlControl: FormControl;
  onlySelectedEditionsControl: FormControl;
  isSearching: boolean = false;
  isLoadingMore: boolean = false;
  hasMoreResults: boolean = true;
  articleSearchResults: IDashboardContent[] = [];
  pageNumber: number = this.DEFAULT_PAGE_NUMBER;

  constructor(
    private contentService: ContentService,
    private dialogService: DialogService
  ) {}

  ngOnInit(): void {
    this.searchBarControl = new FormControl('');
    this.addArticleByUrlControl = new FormControl('');
    this.onlySelectedEditionsControl = new FormControl(true);

    this.onlySelectedEditionsControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        filter((_) => !!this.searchBarControl.value.trim()),
        tap(() => {
          this.isSearching = true;
          this.pageNumber = this.DEFAULT_PAGE_NUMBER;
        }),
        switchMap((_) => this._searchArticles())
      )
      .subscribe((searchResults: ISearchContentResponse) => {
        this.isSearching = false;
        this.articleSearchResults = searchResults.contentResults;
        this.searchList = searchResults.contentResults.map(
          (p) => new SearchArticleResult(p.contentId, p.title, p.publishedDate)
        );
      });

    this.searchBarControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        debounceTime(500),
        filter((searchTerm) => !!searchTerm.trim()),
        tap(() => {
          this.isSearching = true;
          this.pageNumber = this.DEFAULT_PAGE_NUMBER;
        }),
        switchMap((_) => this._searchArticles())
      )
      .subscribe((searchResults: ISearchContentResponse) => {
        this.isSearching = false;
        this.articleSearchResults = searchResults.contentResults;
        this.searchList = searchResults.contentResults.map(
          (p) => new SearchArticleResult(p.contentId, p.title, p.publishedDate)
        );
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  private _searchArticles(): Observable<ISearchContentResponse> {
    const searchTerm = this.searchBarControl.value;
    if (!searchTerm) return EMPTY;
    if (searchTerm) {
      const request = this.buildGetRequest(searchTerm);
      return this.contentService.getFiltered$(request).pipe(
        tap((searchResponse: ISearchContentResponse) => {
          if (
            searchResponse.totalMatches <=
            request.pageNumber * request.pageSize
          ) {
            this.hasMoreResults = false;
          } else {
            this.hasMoreResults = true;
          }
        })
      );
    }
  }

  buildGetRequest(searchValue: string): SearchArticleRequest {
    const request = new SearchArticleRequest();
    request.pageNumber = this.pageNumber;
    request.pageSize = 10;
    request.extendedSearch = true;
    request.sortByPublishedDateDesc = true;
    request.searchText = searchValue;
    // Only return published articles so we don't link unavailable content
    request.articleStatuses = [ArticleStatus[ArticleStatus.Published]];
    if (this.onlySelectedEditionsControl.value) {
      request.deliveryTargetCodeFilter = this.editions;
    }

    return request;
  }

  private _addRelatedContent(relatedContent: IRelatedContent) {
    if (
      this.relatedContents.value.filter(
        (x) => x.contentId == relatedContent.contentId
      ).length === 0
    ) {
      this.relatedContents.push(new FormControl(relatedContent));
    }
  }

  handleArticleSelected($event: ISearchArticleResult) {
    let article = this.articleSearchResults.find(
      (p) => p.contentId === $event.id
    );
    let edition = article.deliveryTargetCodes.find((p) =>
      this.editions.includes(p)
    );
    if (!edition && article.deliveryTargetCodes.length > 0)
      edition = article.deliveryTargetCodes[0];
    var link = article.urlsByDeliveryTargetCodes[edition];

    if (typeof article.contentType === 'string') {
      var enumContentType: ContentType =
        ContentType[article.contentType as keyof typeof ContentType];
      article.contentType = enumContentType;
    }

    let content = new RelatedContent(
      parseInt(article.contentId.replace('Content_', '')),
      article.title,
      article.deliveryTargetCodes,
      edition,
      link,
      '',
      article.contentType
    );
    this._addRelatedContent(content);
  }

  onRemove(item: IRelatedContent) {
    let indexToRemove = this.relatedContents.value.findIndex(
      (x) => x.contentId == item.contentId
    );
    if (indexToRemove > -1) {
      this.handleRemoveLink.emit({
        regex:
          '(<a[^>]*data-content-id="' +
          item.contentId +
          '"[^>]*>(.*?)</[a|A]>)',
        newValue: '$2',
      });
      this.relatedContents.removeAt(indexToRemove);
    }
  }

  onAddToBody(item: IRelatedContent) {
    this.handleAddToBody.emit({
      id: item.contentId.toString(),
      name: item.title,
      dataLinkType: DataLinkType.RELATED_CONTENT,
      code: item.deliveryTargetCode,
    });
  }

  onInfoClick(item: IRelatedContent) {
    window.open(item.link, '_blank');
  }

  addArticleByUrl() {
    this.contentService
      .getByUrl$(this.addArticleByUrlControl.value)
      .pipe(
        catchError((x) => {
          this.dialogService.openErrorDialog({
            message: `There was an error loading data for this URL: ${x.errorMessage.errorReason}`,
          });
          return EMPTY;
        })
      )
      .subscribe((article: IParseArticleUrlResponse) => {
        let edition = article.deliveryTargetCodes.find((p) =>
          this.editions.includes(p)
        );
        if (!edition && article.deliveryTargetCodes.length > 0)
          edition = article.deliveryTargetCodes[0];
        let content = new RelatedContent(
          article.contentID,
          article.title,
          article.deliveryTargetCodes,
          edition,
          this.addArticleByUrlControl.value,
          '',
          article.contentType
        );
        this._addRelatedContent(content);
        this.addArticleByUrlControl.setValue('');
      });
  }

  loadMore() {
    this.pageNumber++;
    this.isLoadingMore = true;
    this._searchArticles()
      .pipe(finalize(() => (this.isLoadingMore = false)))
      .subscribe((searchResults: ISearchContentResponse) => {
        this.articleSearchResults.push(...searchResults.contentResults);
        this.searchList.push(
          ...searchResults.contentResults.map(
            (p) =>
              new SearchArticleResult(p.contentId, p.title, p.publishedDate)
          )
        );
      });
  }

  clearSearch() {
    this.searchList = [];
    this.searchBarControl.setValue('');
  }
}
